import { useState, useMemo, useCallback, useEffect } from 'react';
import type { EntityChangeDto } from '@/types/core/Simulation';

const STORAGE_KEY = 'nexo-simulation-filters';
const DEFAULT_HIDDEN: string[] = ['AuditLogAction', 'AuditLog'];

/**
 * Hook for filtering simulation entity changes.
 * Hidden entity types are persisted to localStorage.
 * Change type toggles are ephemeral (reset each session).
 */
export function useSimulationFilters(entityChanges: EntityChangeDto[]) {
  // Persisted: which entity types are hidden
  const [hiddenEntityTypes, setHiddenEntityTypes] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : DEFAULT_HIDDEN;
    } catch {
      return DEFAULT_HIDDEN;
    }
  });

  // Ephemeral: which change types are hidden
  const [hiddenChangeTypes, setHiddenChangeTypes] = useState<string[]>([]);

  // Sync hidden entity types to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(hiddenEntityTypes));
    } catch (error) {
      console.warn('Failed to save simulation filters to localStorage:', error);
    }
  }, [hiddenEntityTypes]);

  // Compute counts from unfiltered data
  const counts = useMemo(() => {
    const byChangeType: Record<string, number> = {};
    const byEntityType: Record<string, number> = {};
    for (const change of entityChanges) {
      byChangeType[change.changeType] = (byChangeType[change.changeType] ?? 0) + 1;
      byEntityType[change.entityType] = (byEntityType[change.entityType] ?? 0) + 1;
    }
    return { byChangeType, byEntityType };
  }, [entityChanges]);

  // Derive which types are visible (not hidden)
  const changeTypeFilters = useMemo(() => {
    const types = ['Added', 'Modified', 'Deleted'] as const;
    return Object.fromEntries(types.map((t) => [t, !hiddenChangeTypes.includes(t)])) as Record<
      string,
      boolean
    >;
  }, [hiddenChangeTypes]);

  const entityTypeFilters = useMemo(() => {
    const allTypes = Object.keys(counts.byEntityType);
    return Object.fromEntries(allTypes.map((t) => [t, !hiddenEntityTypes.includes(t)])) as Record<
      string,
      boolean
    >;
  }, [counts.byEntityType, hiddenEntityTypes]);

  // Filter entity changes
  const filteredChanges = useMemo(() => {
    return entityChanges.filter(
      (c) => !hiddenChangeTypes.includes(c.changeType) && !hiddenEntityTypes.includes(c.entityType),
    );
  }, [entityChanges, hiddenChangeTypes, hiddenEntityTypes]);

  const toggleChangeType = useCallback((type: string) => {
    setHiddenChangeTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  }, []);

  const toggleEntityType = useCallback((type: string) => {
    setHiddenEntityTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  }, []);

  return {
    changeTypeFilters,
    entityTypeFilters,
    toggleChangeType,
    toggleEntityType,
    filteredChanges,
    counts,
  };
}
